import axios from "axios";
import type { ListParams, Paginated } from "../api";
import type {
  CreateExportPayload,
  ExportJob,
} from "@/types/exports";

// ——— Exports ———

export async function exportDoctors(params?: ListParams): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/central/doctors/export", params, "doctors.xlsx");
}

export async function exportSessions(params?: ListParams): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/central/sessions/export", params, "sessions.xlsx");
}

export async function exportReviewers(params?: ListParams): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/central/reviewers/export", params, "reviewers.xlsx");
}

export async function exportConsents(params?: ListParams): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/central/consents/export", params, "consents.xlsx");
}

export async function exportAdminReport(): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/admin/reports/export", undefined, "admin-report.xlsx");
}

// ——— Consent files ZIP ———

export async function exportConsentFiles(params?: ListParams): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/central/consents/export-files", params, "consents.zip");
}

export async function exportSessionConsentFiles(sessionId: number): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi(
    `/central/sessions/${sessionId}/consents/export-files`,
    undefined,
    `session-${sessionId}-consents.zip`,
  );
}

// ——— Admin data export hub ———

export type AdminExportEntity = "doctors" | "sessions" | "reviewers" | "centrals" | "subscriptions";

export async function exportAdminEntity(
  entity: AdminExportEntity,
  params?: ListParams,
): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi(`/admin/${entity}/export`, params, `${entity}.xlsx`);
}

// ——— Export Center: async export jobs (§A4) ———
//
// Portal-aware async export jobs. `portal` is "central" | "admin"; the path
// resolves to `/central/exports` or `/admin/exports`. These target the §A4
// endpoints a parallel effort is adding. Until those land, list/poll calls fall
// back to a small mock so the Export Center renders today (see MOCK_* below).

export type ExportPortal = "central" | "admin";

function portalBase(portal: ExportPortal): string {
  return `/${portal}/exports`;
}

/** True for a 404 or a network/connection error — i.e. the endpoint is absent. */
function isMissingEndpoint(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  // No response at all → network/CORS/offline. 404 → route not deployed yet.
  return !error.response || error.response.status === 404;
}

export async function createExport(
  portal: ExportPortal,
  payload: CreateExportPayload,
): Promise<{ id: ExportJob["id"] }> {
  const { post } = await import("./helpers");
  try {
    const body = await post<{ id?: ExportJob["id"]; data?: { id: ExportJob["id"] } }>(
      portalBase(portal),
      payload,
    );
    return { id: body.id ?? body.data?.id ?? mockEnqueue(payload).id };
  } catch (error) {
    // ——— remove once §A4 lands ———
    if (isMissingEndpoint(error)) return mockEnqueue(payload);
    throw error;
  }
}

export async function fetchExports(
  portal: ExportPortal,
  params?: ListParams,
): Promise<Paginated<ExportJob>> {
  const { get, buildQuery } = await import("./helpers");
  try {
    return await get<Paginated<ExportJob>>(`${portalBase(portal)}${buildQuery(params)}`);
  } catch (error) {
    // ——— remove once §A4 lands ———
    if (isMissingEndpoint(error)) return mockList(params);
    throw error;
  }
}

export async function fetchExport(
  portal: ExportPortal,
  id: ExportJob["id"],
): Promise<ExportJob | undefined> {
  const { get } = await import("./helpers");
  try {
    const body = await get<ExportJob | { data: ExportJob }>(`${portalBase(portal)}/${id}`);
    return "data" in body ? body.data : body;
  } catch (error) {
    // ——— remove once §A4 lands ———
    if (isMissingEndpoint(error)) return mockList().data.find((j) => j.id === id);
    throw error;
  }
}

export async function downloadExport(
  portal: ExportPortal,
  id: ExportJob["id"],
): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi(`${portalBase(portal)}/${id}/download`, undefined, `export-${id}`);
}

export async function retryExport(
  portal: ExportPortal,
  id: ExportJob["id"],
): Promise<void> {
  const { post } = await import("./helpers");
  try {
    await post(`${portalBase(portal)}/${id}/retry`, {});
  } catch (error) {
    // ——— remove once §A4 lands: swallow so the optimistic UI isn't undone ———
    if (!isMissingEndpoint(error)) throw error;
  }
}

export async function deleteExport(
  portal: ExportPortal,
  id: ExportJob["id"],
): Promise<void> {
  const { del } = await import("./helpers");
  try {
    await del(`${portalBase(portal)}/${id}`);
  } catch (error) {
    // ——— remove once §A4 lands ———
    if (!isMissingEndpoint(error)) throw error;
  }
}

// ——— Mock fallback ——— remove this whole block once §A4 lands ———
// A few representative jobs spanning all four statuses so the Export Center is
// fully demonstrable (status badges, progress bar, download/retry/delete) before
// the backend ships. The "processing" job advances on each poll so live polling
// is visible. Kept module-local; no persistence.

let mockSeq = 1000;
const mockJobs: ExportJob[] = [
  {
    id: mockSeq++,
    kind: "doctors",
    format: "xlsx",
    status: "ready",
    progress: 100,
    total_rows: 482,
    file_size: 91_544,
    created_at: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    expires_at: new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString(),
  },
  {
    id: mockSeq++,
    kind: "sessions",
    format: "csv",
    status: "processing",
    progress: 38,
    total_rows: 1240,
    created_at: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
  },
  {
    id: mockSeq++,
    kind: "consents",
    format: "zip",
    status: "queued",
    progress: 0,
    created_at: new Date(Date.now() - 1000 * 30).toISOString(),
  },
  {
    id: mockSeq++,
    kind: "reviewers",
    format: "pdf",
    status: "failed",
    progress: 0,
    error: "Source dataset timed out",
    created_at: new Date(Date.now() - 1000 * 60 * 40).toISOString(),
  },
];

function mockEnqueue(payload: CreateExportPayload): { id: ExportJob["id"] } {
  const id = mockSeq++;
  mockJobs.unshift({
    id,
    kind: payload.kind,
    format: payload.format,
    status: "queued",
    progress: 0,
    created_at: new Date().toISOString(),
  });
  return { id };
}

/** Advance any in-flight mock jobs a little so polling shows live movement. */
function tickMock(): void {
  for (const job of mockJobs) {
    if (job.status === "queued") {
      job.status = "processing";
      job.progress = 5;
    } else if (job.status === "processing") {
      job.progress = Math.min(100, job.progress + 18 + Math.floor(Math.random() * 10));
      if (job.progress >= 100) {
        job.status = "ready";
        job.progress = 100;
        job.total_rows = job.total_rows ?? 256;
        job.file_size = job.file_size ?? 64_000;
        job.expires_at = new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString();
      }
    }
  }
}

function mockList(params?: ListParams): Paginated<ExportJob> {
  tickMock();
  const perPage = params?.per_page ?? 20;
  const page = params?.page ?? 1;
  const sorted = [...mockJobs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const start = (page - 1) * perPage;
  return {
    data: sorted.slice(start, start + perPage),
    meta: {
      total: sorted.length,
      per_page: perPage,
      current_page: page,
      last_page: Math.max(1, Math.ceil(sorted.length / perPage)),
    },
  };
}
